import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

export const AddOrganizationDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const close = (v) => {
    if (!v) {
      if (result) onCreated?.();
      setName(''); setAdminEmail(''); setResult(null);
    }
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/super-admin/organizations`, { name, admin_email: adminEmail || null });
      setResult(res.data);
      toast.success(`Organization "${res.data.name}" created`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create organization');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="add-org-dialog">
        <DialogHeader>
          <DialogTitle>Add Organization</DialogTitle>
          <DialogDescription>New organizations start empty. Optionally invite their first Admin.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="add-org-result">
            <p className="text-sm text-slate-700"><b>{result.name}</b> has been created.</p>
            {result.invite ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
                <p className="text-slate-700">Admin invite for <b>{result.invite.email}</b>:</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="text-lg font-mono font-bold tracking-widest" data-testid="add-org-invite-code">{result.invite.invite_code}</code>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(result.invite.invite_code); toast.success('Copied'); }} data-testid="add-org-copy-code">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Expires {new Date(result.invite.expires_at).toLocaleDateString('en-GB')}. They sign up with this email and code.</p>
              </div>
            ) : (
              <p className="text-xs text-slate-500">No admin invited. Open the organization and invite users from Organization Settings.</p>
            )}
            <div className="flex justify-end"><Button onClick={() => close(false)} data-testid="add-org-done">Done</Button></div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="org-name">Organization Name *</Label>
              <Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1" data-testid="add-org-name" />
            </div>
            <div>
              <Label htmlFor="org-admin-email">First Admin Email (optional)</Label>
              <Input id="org-admin-email" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@client.com" className="mt-1" data-testid="add-org-admin-email" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => close(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !name.trim()} data-testid="add-org-submit">{saving ? 'Creating…' : 'Create Organization'}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
